import React, { useCallback, useRef } from "react";
import { Box, Avatar,  Typography, Tooltip } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import "./ProfilePictureUpload.css";

const ProfilePictureUpload = ({
    onFileSelect,
    onRemove,
    accept = "image/jpeg,image/png,image/jpg",
    maxSize = 2 * 1024 * 1024, // 2MB default
    preview = null,
    firstName = "",
    lastName = "",
    error = null,
    disabled = false,
    size = 120,
    removeIconSize = 80,
}) => {
    const fileInputRef = useRef(null);
    const [localError, setLocalError] = React.useState(null);

    const getInitials = () => {
        const first = firstName?.charAt(0)?.toUpperCase() || "";
        const last = lastName?.charAt(0)?.toUpperCase() || "";
        return first + last || "U";
    };

    const validateFile = useCallback((file) => {
        if (!file) return { valid: false, error: "No file selected" };

        // Check file size
        if (file.size > maxSize) {
            const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
            return {
                valid: false,
                error: `File size exceeds ${maxSizeMB}MB limit`,
            };
        }

        // Check file type if accept is specified
        if (accept && accept !== "*") {
            const acceptedTypes = accept.split(",").map((type) => type.trim());
            const fileType = file.type;
            const fileName = file.name.toLowerCase();

            const isAccepted = acceptedTypes.some((type) => {
                if (type.startsWith(".")) {
                    return fileName.endsWith(type.toLowerCase());
                } else if (type.includes("/*")) {
                    const baseType = type.split("/")[0];
                    return fileType.startsWith(baseType + "/");
                } else {
                    return fileType === type;
                }
            });

            if (!isAccepted) {
                return {
                    valid: false,
                    error: `File type not supported. Accepted types: ${accept}`,
                };
            }
        }

        return { valid: true, error: null };
    }, [maxSize, accept]);

    const handleFileSelect = useCallback(
        (file) => {
            const validation = validateFile(file);
            if (!validation.valid) {
                setLocalError(validation.error);
                setTimeout(() => setLocalError(null), 3000);
                return;
            }

            setLocalError(null);
            onFileSelect(file);
        },
        [validateFile, onFileSelect]
    );

    const handleFileInputChange = useCallback(
        (e) => {
            if (disabled) return;

            const files = Array.from(e.target.files);
            if (files.length > 0) {
                handleFileSelect(files[0]);
            }
            // Reset input value to allow selecting the same file again
            e.target.value = "";
        },
        [disabled, handleFileSelect]
    );

    const handleClick = useCallback(() => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    }, [disabled]);

    const handleRemove = useCallback(
        (e) => {
            e.stopPropagation();
            if (onRemove) {
                onRemove();
            }
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        },
        [onRemove]
    );

    const hasPreview = preview && (preview.startsWith("data:image") || preview.startsWith("http"));

    return (
        <Box className="profile-picture-upload-container">
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 2,
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        cursor: disabled ? "not-allowed" : "pointer",
                    }}
                    onClick={handleClick}
                >
                    <Avatar
                        src={hasPreview ? preview : null}
                        sx={{
                            width: size,
                            height: size,
                            bgcolor: hasPreview ? "transparent" : "grey.300",
                            fontSize: size * 0.4,
                            fontWeight: 600,
                            color: "text.secondary",
                            border: "3px solid",
                            borderColor: error || localError ? "error.main" : "divider",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                borderColor: disabled ? "divider" : "primary.main",
                                transform: disabled ? "none" : "scale(1.05)",
                            },
                        }}
                    >
                        {!hasPreview && getInitials()}
                    </Avatar>

                    {/* Camera Icon */}
                    {!disabled && (
                        <Box
                            className="camera-icon-container"
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                right: 0,
                                width: size * 0.35,
                                height: size * 0.35,
                                borderRadius: "50%",
                                bgcolor: "grey.700",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "3px solid",
                                borderColor: "background.paper",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    bgcolor: "grey.800",
                                    transform: "scale(1.1)",
                                },
                            }}
                        >
                            <CameraAltIcon
                                sx={{
                                    fontSize: size * 0.2,
                                    color: "white",
                                }}
                            />
                        </Box>
                    )}

                    {/* Remove Button */}
                    {hasPreview && !disabled && (
                        <Tooltip title="Remove picture">
                            <Box
                                className="remove-button"
                                onClick={handleRemove}
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    right: 0,
                                    width: removeIconSize * 0.35,
                                    height: removeIconSize * 0.35,
                                    borderRadius: "50%",
                                    bgcolor: "#f44336",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "3px solid",
                                    borderColor: "background.paper",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        bgcolor: "#d32f2f",
                                        transform: "scale(1.1)",
                                    },
                                }}
                            >
                                <CloseIcon 
                                    sx={{ 
                                        fontSize: size * 0.2, 
                                        color: "white",
                                        fontWeight: "bold"
                                    }} 
                                />
                            </Box>
                        </Tooltip>
                    )}
                </Box>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept={accept}
                    onChange={handleFileInputChange}
                    style={{ display: "none" }}
                    disabled={disabled}
                />

                {(error || localError) && (
                    <Typography
                        variant="caption"
                        color="error"
                        sx={{
                            textAlign: "center",
                            fontWeight: 500,
                            maxWidth: size + 50,
                        }}
                    >
                        {error || localError}
                    </Typography>
                )}

                <Typography
                    variant="caption"
                    sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        maxWidth: size + 50,
                    }}
                >
                    {accept !== "*" && `Accepted: ${accept.replace(/image\//g, "").replace(/,/g, ", ")}`}
                    <br/>
                    {maxSize && `Max size: ${(maxSize / (1024 * 1024)).toFixed(2)}MB`}
                </Typography>
            </Box>
        </Box>
    );
};

export default ProfilePictureUpload;

